import { useState } from "react";
import { Send, ArrowRight, MessageCircle, Check } from "lucide-react";
import { WA_LINK } from "./shared";

export default function LeadFormSection({ city, lang, t }) {
  const f = t.leadFields;
  const [form, setForm] = useState({ name: "", contact: "", wedding_date: "", guests: "", budget: "", message: "" });
  const [state, setState] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || form.name.trim().length < 2 || !form.contact.trim() || form.contact.trim().length < 4) {
      setErr(f.required);
      return;
    }
    setState("sending");
    try {
      // Same-origin fetch — works on preview and yuccaflower.com without CORS preflight
      const r = await fetch(`/api/leads/wedding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          city: city.slug,
          lang,
          source_page: typeof window !== "undefined" ? window.location.pathname : "",
        }),
      });
      if (!r.ok) throw new Error("bad");
      setState("success");
    } catch (_e) {
      setState("error");
    }
  };

  return (
    <section className="relative bg-[#0A0807] py-16 sm:py-24 lg:py-32 overflow-hidden" data-testid="weddings-leadform">
      <div className="absolute inset-0">
        <img src={city.heroImage} alt="" aria-hidden="true"
             className="absolute inset-0 w-full h-full object-cover opacity-25"
             style={{ filter: "blur(6px) brightness(0.5)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0807]/85 via-[#0A0807]/75 to-[#0A0807]" />
      </div>
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(#D4B896 0.5px, transparent 0.5px)", backgroundSize: "26px 26px" }} />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-10">
        <div className="text-center mb-10 sm:mb-12">
          <p className="text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.35em] text-[#F5C77A] mb-3 sm:mb-4 font-light">{t.leadEyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {t.leadTitle} <span className="italic text-[#F5C77A]">{t.leadTitleAccent}</span>
          </h2>
          <p className="text-sm sm:text-base text-white/65 font-light mt-4 sm:mt-5 max-w-xl mx-auto leading-relaxed">{t.leadSubtitle}</p>
        </div>

        {state === "success" ? (
          <div className="bg-white/[0.05] border border-[#F5C77A]/40 backdrop-blur-md rounded-sm p-8 sm:p-12 text-center" data-testid="weddings-lead-success">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#F5C77A] text-[#0A0807] flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
            </div>
            <h3 className="text-2xl sm:text-3xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{f.successTitle}</h3>
            <p className="text-sm sm:text-base text-white/70 font-light leading-relaxed max-w-md mx-auto">{f.successText}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 sm:space-y-5" data-testid="weddings-lead-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <input
                type="text" required placeholder={f.name} value={form.name} onChange={update("name")}
                className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm placeholder-white/40 rounded-sm transition"
                data-testid="lead-name"
              />
              <input
                type="text" required placeholder={f.contact} value={form.contact} onChange={update("contact")}
                className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm placeholder-white/40 rounded-sm transition"
                data-testid="lead-contact"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              <input
                type="text" placeholder={f.date} value={form.wedding_date} onChange={update("wedding_date")}
                className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm placeholder-white/40 rounded-sm transition"
                data-testid="lead-date"
              />
              <select value={form.guests} onChange={update("guests")}
                      className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm rounded-sm transition appearance-none"
                      data-testid="lead-guests">
                <option value="" className="bg-[#0A0807]">{f.guests}</option>
                {f.guestsOptions.map(o => <option key={o} value={o} className="bg-[#0A0807]">{o}</option>)}
              </select>
              <select value={form.budget} onChange={update("budget")}
                      className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm rounded-sm transition appearance-none"
                      data-testid="lead-budget">
                <option value="" className="bg-[#0A0807]">{f.budget}</option>
                {f.budgetOptions.map(o => <option key={o} value={o} className="bg-[#0A0807]">{o}</option>)}
              </select>
            </div>
            <textarea
              rows={4} placeholder={f.message} value={form.message} onChange={update("message")}
              className="w-full px-4 py-3.5 bg-white/[0.05] border border-white/15 focus:border-[#F5C77A] focus:bg-white/[0.08] outline-none text-white text-sm placeholder-white/40 rounded-sm transition resize-none"
              data-testid="lead-message"
            />

            {err && <p className="text-[#F5C77A] text-sm font-light text-center" data-testid="lead-error">{err}</p>}

            <div className="flex flex-col sm:flex-row items-center sm:items-stretch gap-3 sm:gap-4 pt-1 sm:pt-2">
              <button
                type="submit" disabled={state === "sending"}
                className="group inline-flex items-center justify-center gap-2 sm:gap-3 w-full sm:w-auto px-8 sm:px-10 py-4 bg-gradient-to-r from-[#FFE5A8] via-[#F5C77A] to-[#E0A954] hover:from-[#FFEDB8] hover:to-[#E8B86C] text-[#0A0807] text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.28em] font-semibold transition-all duration-500 rounded-sm shadow-[0_12px_40px_-10px_rgba(245,199,122,0.55)] hover:shadow-[0_18px_55px_-10px_rgba(245,199,122,0.75)] disabled:opacity-60 disabled:cursor-wait"
                data-testid="lead-submit"
              >
                <Send className="w-4 h-4" />
                <span>{state === "sending" ? f.sending : f.submit}</span>
                {state !== "sending" && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
              </button>
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-4 border border-white/30 text-white/80 hover:text-white hover:border-white/60 text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.25em] font-medium transition rounded-sm">
                <MessageCircle className="w-4 h-4" />WhatsApp
              </a>
            </div>

            {state === "error" && (
              <p className="text-xs sm:text-sm text-white/60 text-center pt-2" data-testid="lead-error-fallback">{f.errorText}</p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
